import { AfterViewInit, Component, DoCheck, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, Validator } from '@angular/forms';
import { IconComponent } from '@libs/components/icons';
import { InputValidationErrors, PasswordCreateInputModel, TextInputModel } from '@libs/form/models';
import { InputBaseComponent } from '../../../models';
import { InputProviderFactory } from '../ng-input-provider';
import { TextComponent } from '../text';

@Component({
  selector: 'ui-form-password-create',
  templateUrl: './password-create.component.html',
  providers: [
    ...InputProviderFactory.getProviders(PasswordCreateComponent, true),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TextComponent,
    ReactiveFormsModule,
    IconComponent,
  ],
})
export class PasswordCreateComponent extends InputBaseComponent implements OnInit, AfterViewInit, Validator, DoCheck {
  private onChange!: (_: unknown) => unknown;
  private onTouched!: () => void;

  @Input()
  public data!: PasswordCreateInputModel;

  public valueAccessor!: never;

  public controlPassword: UntypedFormControl = new UntypedFormControl();
  public controlRepeat: UntypedFormControl = new UntypedFormControl();
  public statePassword!: TextInputModel;
  public stateRepeat!: TextInputModel;
  public isMasked!: boolean;

  public override ngOnInit(): void {
    super.ngOnInit();
    this.prepareStates();

    if (this.onChange === undefined) {
      this.initializeDynamicElement();
    }
  }

  public ngAfterViewInit(): void {
    this.control.updateValueAndValidity();
  }

  public override ngDoCheck(): void {
    super.ngDoCheck();

    if (this.control.touched === this.controlPassword.touched) {
      return;
    }

    if (this.control.touched) {
      this.controlPassword.markAsTouched();
      this.controlRepeat.markAsTouched();
    } else {
      this.controlPassword.markAsUntouched();
      this.controlRepeat.markAsUntouched();
    }
  }

  public onPasswordInput(): void {
    this.onChange(this.controlPassword.value);

    this.controlRepeat.markAsUntouched();
    this.controlRepeat.updateValueAndValidity();
  }

  public onPasswordBlur(): void {
    if (!this.control.touched) {
      this.onTouched();
    }

    if (this.controlPassword.dirty) {
      this.controlRepeat.markAsTouched();
    }
  }

  public onRepeatInput(): void {
    this.onChange(this.controlPassword.value);
  }

  public switchIsMasked(): void {
    this.isMasked = !this.isMasked;
    this.statePassword.type =
      this.statePassword.type === 'password' ? 'text' : 'password';
  }

  public override writeValue(value: unknown): void {
    this.controlPassword.setValue(value);
    this.controlRepeat.setValue(value);
  }

  public override registerOnChange(fn: (_: unknown) => unknown): void {
    this.onChange = fn;
  }

  public override registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public override setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.controlPassword.disable();
      this.controlRepeat.disable();
    } else {
      this.controlPassword.enable();
      this.controlRepeat.enable();
    }
  }

  public override validate(): InputValidationErrors | null {
    if (this.controlPassword.errors !== null) {
      return this.controlPassword.errors;
    }

    if (this.controlRepeat.errors !== null) {
      return this.controlRepeat.errors;
    }

    return null;
  }

  private validateRepeat(): InputValidationErrors | null {
    if (this.controlPassword.value === this.controlRepeat.value) {
      return null;
    }

    return {
      passwordRepeat: true,
    };
  }

  private initializeDynamicElement(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.onChange = this.onChangeDefault;

    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.onTouched = this.onTouchedDefault;

    this.control.addValidators(this.validate.bind(this));
  }

  private onChangeDefault(value: unknown): void {
    this.control.setValue(value);
  }

  private onTouchedDefault(): void {
    this.control.markAsTouched();
  }

  private prepareStates(): void {
    this.isMasked = true;

    this.statePassword = {
      type: 'password',
      name: this.data.name,
      label: this.data.label,
      value: this.data.value,
      attributes: {
        autocomplete: 'new-password',
        ...this.data.attributes,
      },
    };

    this.stateRepeat = {
      type: 'password',
      name: `${this.statePassword.name}-repeat`,
      label: this.data.labelRepeat,
      value: this.data.value,
      attributes: {
        autocomplete: 'new-password',
        required: this.data.attributes?.required,
        readOnly: this.data.attributes?.readOnly,
        disabled: this.data.attributes?.disabled,
      },
    };

    this.controlRepeat.addValidators(this.validateRepeat.bind(this));
  }
}
